// Client de `GET /backoffice/customers/table` (retail-wizard-business).
import { ApiError } from '../../../errors';
import { apiFetch } from '../../../client';
import type {
    CustomersTableApi,
    CustomersTableItem,
    CustomersTablePage,
    CustomersTableQuery,
} from './customersTableContract';

/** El wire puede omitir el cursor y la lista vacía; sin `sources` no es la tabla. */
type CustomersTableWire = Omit<CustomersTablePage, 'nextToken' | 'items' | 'sources'> & {
    nextToken?: string | null;
    items?: CustomersTableItem[];
    sources?: CustomersTablePage['sources'];
};

const BASE_URL = import.meta.env.VITE_API_RETAIL_WIZARD_BASE_URL;

function queryOf(query: CustomersTableQuery): string {
    const params = new URLSearchParams();
    const entries: [string, string | number | undefined][] = [
        ['from', query.from],
        ['to', query.to],
        ['limit', query.limit],
        ['nextToken', query.nextToken],
        ['storeId', query.storeId],
        ['sellerId', query.sellerId],
        ['customerType', query.customerType],
    ];
    for (const [key, value] of entries) {
        if (value !== undefined && value !== '') params.set(key, String(value));
    }
    return params.toString();
}

export const customersTableClient: CustomersTableApi = {
    async getPage(query) {
        // Sin refresh: mientras la ruta no exista, API Gateway responde 403 sin `code` y el refresh
        // cerraría la sesión. La pantalla trata ese 403 como «endpoint ausente».
        const page = await apiFetch<CustomersTableWire>(
            `/backoffice/customers/table?${queryOf(query)}`,
            { baseUrl: BASE_URL, skipRefresh: true },
        );
        // Sin desplegar, `/customers/table` lo atiende `/customers/{id}` con 200 y otra forma: se
        // reporta como ruta ausente para que la pantalla use el respaldo. Se borra con el respaldo.
        if (!page.sources) {
            throw new ApiError(404, {
                code: '',
                date: '',
                msg: 'La tabla de clientes no existe aún.',
            });
        }
        return {
            ...page,
            sources: page.sources,
            nextToken: page.nextToken ?? null,
            items: page.items ?? [],
        };
    },
};
